import { Building2, Check, House, Search } from "lucide-react";

import { CityMap } from "@/features/portal/components/city-map";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

/** Other gyms around town, as quiet dots; the picked one gets the pin. */
const DOTS = ["left-[18%] top-[22%]", "left-[34%] top-[58%]", "left-[71%] top-[18%]", "left-[84%] top-[46%]", "left-[58%] top-[70%]"];

const GYMS = [
  { id: "north", name: () => m.features_gym_one(), detail: () => m.features_gym_one_detail(), distance: "0,8 km", picked: true },
  { id: "old-town", name: () => m.features_gym_two(), detail: () => m.features_gym_two_detail(), distance: "2,1 km" },
];

/**
 * The gym picker over a night map of a city: a search box and the nearest gyms, the picked one
 * pinned on the map with the equipment the plan is built around.
 */
export const GymMap = () => (
  <div aria-hidden className="absolute inset-0">
    <CityMap className="absolute inset-0" />
    <span className="absolute inset-0 bg-[radial-gradient(120%_80%_at_50%_0%,transparent_40%,oklch(0.13_0.004_120)_100%)]" />
    {DOTS.map((spot) => (
      <span key={spot} className={cn("absolute size-3 rounded-full bg-foreground/35 ring-4 ring-foreground/10", spot)} />
    ))}
    <span className="absolute top-[34%] left-[52%] -translate-x-1/2 -translate-y-full">
      <span className="relative flex flex-col items-center">
        <span className="rounded-full bg-primary px-3 py-1.5 text-[13px] font-semibold whitespace-nowrap text-primary-foreground shadow-[0_10px_30px_-6px_var(--primary)]">
          {m.features_gym_one()}
        </span>
        <span className="-mt-px h-3 w-0.5 bg-primary" />
        <span className="relative grid size-4 place-items-center">
          <span className="absolute inset-0 animate-ping rounded-full bg-primary/60 motion-reduce:hidden" />
          <span className="size-4 rounded-full border-[3px] border-[oklch(0.13_0.004_120)] bg-primary" />
        </span>
      </span>
    </span>
    <div className="absolute inset-x-4 bottom-4 rounded-[24px] bg-[oklch(0.2_0.004_120/0.78)] p-2 shadow-[0_24px_60px_-16px_rgba(0,0,0,0.7)] ring-1 ring-white/10 backdrop-blur-xl ring-inset md:inset-x-auto md:left-6 md:w-[360px]">
      <div className="flex h-11 items-center gap-2.5 rounded-[16px] bg-foreground/[0.06] px-3.5 text-[15px] text-muted-foreground">
        <Search className="size-4" />
        {m.features_gym_search()}
      </div>
      <ul className="mt-1">
        {GYMS.map((gym) => (
          <li
            key={gym.id}
            className={cn("flex items-center gap-3 rounded-[16px] px-2.5 py-2.5", gym.picked && "bg-foreground/[0.06]")}
          >
            <span className="grid size-10 shrink-0 place-items-center rounded-[12px] bg-foreground/[0.08]">
              <Building2 className="size-[18px] text-muted-foreground" />
            </span>
            <span className="min-w-0 flex-1 leading-tight">
              <b className="block truncate text-[15px] font-semibold">{gym.name()}</b>
              <small className="block truncate text-[13px] text-muted-foreground">{gym.detail()}</small>
            </span>
            {gym.picked ? (
              <span className="grid size-6 place-items-center rounded-full bg-primary text-primary-foreground">
                <Check className="size-3.5" strokeWidth={3} />
              </span>
            ) : (
              <small className="text-[13px] text-muted-foreground tabular-nums">{gym.distance}</small>
            )}
          </li>
        ))}
        <li className="flex items-center gap-3 rounded-[16px] px-2.5 py-2.5">
          <span className="grid size-10 shrink-0 place-items-center rounded-[12px] bg-foreground/[0.08]">
            <House className="size-[18px] text-muted-foreground" />
          </span>
          <b className="text-[15px] font-semibold text-muted-foreground">{m.features_gym_home()}</b>
        </li>
      </ul>
    </div>
  </div>
);
